import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  Divider,
  Typography,
  Avatar,
  Chip,
  IconButton,
  alpha,
  useTheme,
  Zoom,
  Tooltip,
  Stack,
  Paper,
} from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import DiamondIcon from '@mui/icons-material/Diamond';
import WavesIcon from '@mui/icons-material/Waves';
import { NavItem } from './AppShell';

// Animations (reuse from AppShell)
const floatAnimation = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-5px); }
  100% { transform: translateY(0px); }
`;

// Styled components (consistent with AppShell)
const GlassDrawer = styled(Drawer)(({ theme }) => ({
  '& .MuiDrawer-paper': {
    background: `linear-gradient(135deg, ${alpha(
      theme.palette.background.paper,
      0.98
    )} 0%, ${alpha(theme.palette.primary.dark, 0.1)} 100%)`,
    backdropFilter: 'blur(20px)',
    borderRight: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
    boxShadow: `10px 0 30px ${alpha(theme.palette.common.black, 0.1)}`,
    marginTop: 0, // We'll handle spacing inside
  },
}));

const NavItemButton = styled(ListItemButton, {
  shouldForwardProp: (prop) => prop !== 'active' && prop !== 'depth',
})<{ active?: boolean; depth?: number }>(({ theme, active, depth = 0 }) => ({
  margin: theme.spacing(0.5, 1),
  padding: theme.spacing(1.2, 2),
  borderRadius: theme.spacing(1.5),
  position: 'relative',
  overflow: 'hidden',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',

  ...(active && {
    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
    color: theme.palette.primary.contrastText,
    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
    transform: 'translateX(4px)',

    '& .MuiListItemIcon-root': {
      color: theme.palette.primary.contrastText,
    },

    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: `linear-gradient(135deg, ${alpha(
        theme.palette.common.white,
        0.2
      )} 0%, ${alpha(theme.palette.common.white, 0)} 100%)`,
      zIndex: 1,
    },
  }),

  '&:hover': {
    transform: active ? 'translateX(4px)' : 'translateX(2px)',
    backgroundColor: active ? undefined : alpha(theme.palette.primary.main, 0.08),

    '& .MuiListItemIcon-root': {
      color: theme.palette.primary.main,
    },
  },

  '& .MuiListItemIcon-root': {
    minWidth: depth === 0 ? 40 : 32,
    transition: 'all 0.3s ease',
  },

  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `linear-gradient(90deg, transparent, ${alpha(
      theme.palette.common.white,
      0.1
    )}, transparent)`,
    transform: 'translateX(-100%)',
    transition: 'transform 0.6s ease',
  },

  '&:hover::after': {
    transform: 'translateX(100%)',
  },
}));

const LogoContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2, 2, 1, 2),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
}));

const AnimatedAvatar = styled(Avatar)(({ theme }) => ({
  animation: `${floatAnimation} 3s ease-in-out infinite`,
  border: `2px solid ${theme.palette.primary.main}`,
  boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
}));

export interface SidebarProps {
  navItems: NavItem[];
  open: boolean;
  onClose: () => void;
  width?: number;
  title?: string;
  user?: {
    name: string;
    role: string;
    avatar?: string;
  };
}

const Sidebar: React.FC<SidebarProps> = ({
  navItems,
  open,
  onClose,
  width = 280,
  title = 'Resort Management',
  user,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  const handleNavClick = (item: NavItem) => {
    if (item.path && !item.disabled) {
      navigate(item.path);
      onClose();
    }
  };

  const isActive = (item: NavItem) => {
    return item.path === location.pathname;
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const renderNavItems = (items: NavItem[], depth = 0) => {
    return items.map((item) => (
      <Zoom in key={item.label} style={{ transitionDelay: `${depth * 50}ms` }}>
        <ListItem disablePadding sx={{ display: 'block' }}>
          <Tooltip title={item.label} placement="right" arrow>
            <NavItemButton
              active={isActive(item)}
              depth={depth}
              onClick={() => handleNavClick(item)}
              disabled={item.disabled}
              sx={{
                minHeight: 48,
                justifyContent: 'initial',
                px: 2,
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: depth === 0 ? 40 : 32,
                  mr: 2,
                }}
              >
                {item.icon || <WavesIcon />}
              </ListItemIcon>
              <ListItemText
                primary={item.label}
                primaryTypographyProps={{
                  variant: 'body2',
                  fontWeight: isActive(item) ? 600 : 400,
                }}
              />
            </NavItemButton>
          </Tooltip>
          {item.children && (
            <Box sx={{ pl: 2 }}>
              {renderNavItems(item.children, depth + 1)}
            </Box>
          )}
        </ListItem>
      </Zoom>
    ));
  };

  return (
    <GlassDrawer
      variant="temporary"
      open={open}
      onClose={onClose}
      ModalProps={{ keepMounted: true }}
      sx={{
        '& .MuiDrawer-paper': {
          width,
          boxSizing: 'border-box',
        },
      }}
    >
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Header with Logo and Close Button */}
        <LogoContainer>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DiamondIcon sx={{ color: 'primary.main', fontSize: 28 }} />
            <Typography
              variant="h6"
              sx={{
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                fontWeight: 700,
              }}
            >
              {title}
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </LogoContainer>

        {/* Navigation Items */}
        <Box sx={{ flex: 1, overflow: 'auto', py: 2, px: 1 }}>
          <List>{renderNavItems(navItems)}</List>
        </Box>

        {/* User Section (if provided) */}
        {user && (
          <Box sx={{ p: 2, borderTop: `1px solid ${alpha(theme.palette.primary.main, 0.1)}` }}>
            <Paper
              elevation={0}
              sx={{
                p: 1.5,
                borderRadius: 2,
                background: `linear-gradient(135deg, ${alpha(
                  theme.palette.primary.main,
                  0.1
                )} 0%, ${alpha(theme.palette.secondary.main, 0.1)} 100%)`,
              }}
            >
              <Stack direction="row" spacing={1.5} alignItems="center">
                <AnimatedAvatar src={user.avatar}>
                  {getInitials(user.name)}
                </AnimatedAvatar>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2" fontWeight={600}>
                    {user.name}
                  </Typography>
                  <Chip
                    label={user.role}
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: '0.65rem',
                      background: alpha(theme.palette.primary.main, 0.1),
                      color: 'primary.main',
                    }}
                  />
                </Box>
              </Stack>
            </Paper>
          </Box>
        )}
      </Box>
    </GlassDrawer>
  );
};

export default Sidebar;
